(function () {
  if (window.YesnapEcommerceTracking?.initialized) return;

  const DEBUG = new URLSearchParams(window.location.search).has('yn_debug_tracking');
  const state = {
    initialized: true,
    counter: 0,
    pendingAdds: [],
    sentEventIds: new Set(),
    sentSignatures: new Map(),
    resolvedGroups: new Map(),
    standardAdds: new Map(),
    standardEventsPromise: null,
    beginCheckoutLocked: false,
    fetchPatched: false,
    xhrPatched: false,
  };

  window.dataLayer = window.dataLayer || [];
  const nativeFetch = window.fetch.bind(window);

  function eventId(prefix) {
    if (window.crypto?.randomUUID) return `${prefix}_${window.crypto.randomUUID()}`;
    state.counter += 1;
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}_${state.counter}`;
  }

  function moneyFromCents(cents) {
    return Number((Number(cents || 0) / 100).toFixed(2));
  }

  function loadStandardEvents() {
    if (!state.standardEventsPromise) {
      state.standardEventsPromise = import('https://cdn.shopify.com/storefront/standard-events.js').catch((error) => {
        if (DEBUG) console.warn('[Yesnap ecommerce] Shopify standard events unavailable', error);
        return null;
      });
    }
    return state.standardEventsPromise;
  }

  function standardEventTarget(target) {
    if (target instanceof Element) {
      return target.closest('[data-yesnap-product-form]') || target;
    }
    return document.querySelector('[data-yesnap-product-form]') || document;
  }

  function settleStandardCartAdd(record) {
    if (!record?.deferred || record.settled || record.status === 'pending') return;

    record.settled = true;
    if (record.status === 'resolved' && record.cart) {
      try {
        record.deferred.resolve({
          cart: record.module.CartLinesUpdateEvent.createCartFromAjaxResponse(record.cart),
          detail: { source: 'yesnap-ecommerce', event_id: record.groupId },
        });
        if (DEBUG) console.info(`[Yesnap ecommerce] shopify:cart:lines-update resolved ${record.groupId}`);
      } catch (error) {
        record.settled = false;
        if (DEBUG) console.warn(`[Yesnap ecommerce] shopify:cart:lines-update resolve failed ${record.groupId}`, error);
      }
      return;
    }

    if (record.status === 'rejected') {
      record.deferred.promise.catch(() => {});
      record.deferred.reject(new Error(record.error || 'Unable to add item'));
    }
  }

  function startStandardCartAdd(groupId, items, target) {
    if (!groupId || !items?.length || state.standardAdds.has(groupId)) return;

    const normalizedItems = items.map(normalizePendingItem).filter(Boolean);
    if (!normalizedItems.length) return;

    const record = {
      groupId,
      items: normalizedItems,
      target: standardEventTarget(target),
      status: 'pending',
      cart: null,
      error: '',
      module: null,
      deferred: null,
      settled: false,
      createdAt: Date.now(),
    };
    state.standardAdds.set(groupId, record);

    loadStandardEvents().then((standardEvents) => {
      if (!standardEvents || !state.standardAdds.has(groupId)) return;

      record.module = standardEvents;
      if (record.status === 'rejected') {
        record.target.dispatchEvent(new standardEvents.CartErrorEvent({
          error: record.error || 'Unable to add item',
          code: 'INVALID',
        }));
        return;
      }

      record.deferred = standardEvents.CartLinesUpdateEvent.createPromise();
      record.target.dispatchEvent(new standardEvents.CartLinesUpdateEvent({
        action: 'add',
        context: window.location.pathname.includes('/products/') ? 'product' : 'dialog',
        lines: record.items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: record.deferred.promise,
      }));
      if (DEBUG) console.info(`[Yesnap ecommerce] shopify:cart:lines-update started ${groupId}`);
      settleStandardCartAdd(record);
    });
  }

  function resolveStandardCartAdd(groupId, cart) {
    const record = state.standardAdds.get(groupId);
    if (DEBUG) console.info(`[Yesnap ecommerce] shopify:cart:lines-update resolve attempt ${groupId} ${record?.status || 'missing'}`);
    if (!record || record.status !== 'pending') return;
    record.status = 'resolved';
    record.cart = cart;
    settleStandardCartAdd(record);
  }

  function rejectStandardCartAdd(groupId, error) {
    const record = state.standardAdds.get(groupId);
    if (!record || record.status !== 'pending') return;
    record.status = 'rejected';
    record.error = error || 'Unable to add item';
    settleStandardCartAdd(record);
  }

  function rejectStandardCartAdds(groupIds, error) {
    groupIds.forEach((groupId) => rejectStandardCartAdd(groupId, error));
  }

  function pushDataLayer(eventName, ecommerce, extra) {
    const event_id = extra?.event_id || eventId(eventName);
    if (state.sentEventIds.has(event_id)) return event_id;

    if (eventName === 'add_to_cart') {
      const signature = JSON.stringify({
        event: eventName,
        currency: ecommerce?.currency || '',
        value: ecommerce?.value || 0,
        items: (ecommerce?.items || []).map((item) => ({
          item_id: item.item_id,
          item_variant: item.item_variant,
          price: item.price,
          quantity: item.quantity,
          purchase_type: item.purchase_type,
          selling_plan: item.selling_plan,
        })),
      });
      const previous = state.sentSignatures.get(signature);
      if (previous && Date.now() - previous.sentAt < 2500) return previous.event_id;
      state.sentSignatures.set(signature, { event_id, sentAt: Date.now() });
    }

    state.sentEventIds.add(event_id);
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: eventName, event_id, ecommerce });

    document.dispatchEvent(new CustomEvent('yn:ecommerce', {
      detail: { event: eventName, event_id, ecommerce },
    }));

    if (DEBUG) console.info(`[Yesnap ecommerce] ${eventName} ${event_id}`, JSON.stringify(ecommerce));
    return event_id;
  }

  function readJsonScript(selector) {
    const script = document.querySelector(selector);
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      if (DEBUG) console.warn('[Yesnap ecommerce] Invalid JSON payload', selector, error);
      return null;
    }
  }

  function buildLineItem(line, fallback) {
    const quantity = Number(fallback?.quantity || line?.quantity || 1);
    const sellingPlan = line?.selling_plan_allocation?.selling_plan;
    const variantId = line?.variant_id || fallback?.variantId || '';
    const sku = line?.sku || fallback?.sku || String(variantId);

    return {
      item_id: sku,
      item_name: line?.product_title || fallback?.item_name || '',
      item_brand: line?.vendor || fallback?.item_brand || 'Yesnap',
      item_variant: line?.variant_title || fallback?.item_variant || '',
      item_category: line?.product_type || fallback?.item_category || '',
      price: moneyFromCents(line?.final_price || line?.price || fallback?.price || 0),
      quantity,
      selling_plan: sellingPlan?.name || fallback?.selling_plan || '',
      purchase_type: sellingPlan || fallback?.selling_plan ? 'subscription' : 'one_time',
    };
  }

  function normalizePendingItem(item) {
    const variantId = item?.variantId || item?.variant_id || item?.id || '';
    if (!variantId) return null;

    return {
      variantId: String(variantId),
      quantity: Math.max(Number(item?.quantity || 1), 1),
      sellingPlan: item?.sellingPlan || item?.selling_plan || '',
    };
  }

  function addPendingAdd(item, options) {
    const normalized = normalizePendingItem(item);
    if (!normalized) return '';

    prunePendingAdds();

    if (options?.dedupeFreshFormSubmit) {
      const freshAfter = Date.now() - 5000;
      const existing = state.pendingAdds.find((pending) => (
        pending.createdAt > freshAfter
        && String(pending.variantId) === String(normalized.variantId)
      ));

      if (existing) {
        existing.quantity = normalized.quantity;
        if (normalized.sellingPlan) existing.sellingPlan = normalized.sellingPlan;
        return existing.groupId || existing.event_id;
      }
    }

    const event_id = options?.event_id || eventId('add_to_cart');
    const groupId = options?.groupId || event_id;

    state.pendingAdds.push({
      ...normalized,
      event_id,
      groupId,
      source: options?.source || 'unknown',
      createdAt: Date.now(),
    });

    return groupId;
  }

  function groupPendingAdds(pendingAdds) {
    return pendingAdds.reduce((groups, pending) => {
      const groupId = pending.groupId || pending.event_id;
      if (!groups[groupId]) groups[groupId] = [];
      groups[groupId].push(pending);
      return groups;
    }, {});
  }

  function findCartLine(cart, pending) {
    const lines = Array.isArray(cart?.items) ? cart.items : [];
    const matchingVariantLines = lines.filter((line) => String(line.variant_id) === String(pending.variantId));

    if (!matchingVariantLines.length) return null;
    if (!pending.sellingPlan) {
      return matchingVariantLines.find((line) => !line.selling_plan_allocation) || matchingVariantLines[0];
    }

    return matchingVariantLines.find((line) => {
      const plan = line.selling_plan_allocation?.selling_plan;
      return String(plan?.id || '') === String(pending.sellingPlan);
    }) || matchingVariantLines[0];
  }

  function getFormValue(form, name) {
    const field = form.elements.namedItem(name);
    if (!field || field.disabled) return '';
    if (field instanceof RadioNodeList) return field.value || '';
    return field.value || '';
  }

  function isProductAddForm(form, submitter) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (!getFormValue(form, 'id')) return false;
    if (submitter && submitter.matches('[name="checkout"], .cart__checkout-button')) return false;
    if (form.matches('[data-yesnap-product-form], .shopify-product-form, form[action*="/cart/add"]')) return true;
    return Boolean(form.querySelector('[name="form_type"][value="product"]'));
  }

  function prunePendingAdds() {
    const freshAfter = Date.now() - 20000;
    const resolvedAfter = Date.now() - 60000;
    state.resolvedGroups.forEach((resolvedAt, groupId) => {
      if (resolvedAt <= resolvedAfter) state.resolvedGroups.delete(groupId);
    });
    state.sentSignatures.forEach((entry, signature) => {
      if (entry.sentAt <= resolvedAfter) state.sentSignatures.delete(signature);
    });
    state.standardAdds.forEach((record, groupId) => {
      if (record.createdAt <= resolvedAfter) state.standardAdds.delete(groupId);
    });
    state.pendingAdds = state.pendingAdds.filter((pending) => (
      pending.createdAt > freshAfter
      && !state.resolvedGroups.has(pending.groupId || pending.event_id)
    ));
  }

  function isGroupResolved(groupId) {
    return state.resolvedGroups.has(groupId);
  }

  function resolveGroup(groupId) {
    state.resolvedGroups.set(groupId, Date.now());
    state.pendingAdds = state.pendingAdds.filter((pending) => (
      (pending.groupId || pending.event_id) !== groupId
    ));
  }

  function captureAddToCartSubmit(event) {
    const form = event.target;
    const submitter = event.submitter;
    if (!isProductAddForm(form, submitter)) return;

    prunePendingAdds();

    const pendingEventId = eventId('add_to_cart');
    const pending = {
      event_id: pendingEventId,
      variantId: getFormValue(form, 'id'),
      quantity: Math.max(Number(getFormValue(form, 'quantity') || 1), 1),
      sellingPlan: getFormValue(form, 'selling_plan'),
    };

    const groupId = addPendingAdd(pending, {
      event_id: pendingEventId,
      groupId: pendingEventId,
      source: 'form_submit',
    });
    startStandardCartAdd(groupId, [pending], form);
    form.dataset.ynEcommerceEventId = pending.event_id;
    if (submitter instanceof HTMLElement) submitter.dataset.ynEcommerceEventId = pending.event_id;
  }

  function handleCartUpdate(detail) {
    if (detail?.data?.didError) {
      rejectStandardCartAdds(
        Array.from(new Set(state.pendingAdds.map((pending) => pending.groupId || pending.event_id))),
        detail?.data?.message || 'Unable to add item'
      );
      state.pendingAdds = [];
      return;
    }

    const cart = detail?.resource;
    if (!cart || !Array.isArray(cart.items)) return;

    prunePendingAdds();

    if (!state.pendingAdds.length && Array.isArray(detail?.data?.addedItems) && detail.data.addedItems.length) {
      const groupId = detail.data.event_id || eventId('add_to_cart');
      detail.data.addedItems.forEach((item) => {
        addPendingAdd(item, {
          event_id: groupId,
          groupId,
          source: 'cart_update_added_items',
        });
      });
    }

    if (!state.pendingAdds.length) return;

    const pendingGroups = groupPendingAdds(state.pendingAdds.slice());
    Object.keys(pendingGroups).forEach((groupId) => {
      if (isGroupResolved(groupId)) {
        resolveGroup(groupId);
        return;
      }

      const group = pendingGroups[groupId];
      const items = group.map((pending) => {
        const line = findCartLine(cart, pending);
        return buildLineItem(line, {
          variantId: pending.variantId,
          quantity: pending.quantity,
          selling_plan: pending.sellingPlan,
        });
      });
      const value = Number(items.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2));

      resolveStandardCartAdd(groupId, cart);
      resolveGroup(groupId);
      pushDataLayer('add_to_cart', {
        currency: cart.currency || window.Shopify?.currency?.active || 'USD',
        value,
        items,
      }, { event_id: group[0]?.event_id || groupId });
    });
  }

  async function fetchCart() {
    const retryDelays = [0, 250, 750];
    let lastError = new Error('Cart fetch failed');

    for (const delay of retryDelays) {
      if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));

      try {
        const response = await nativeFetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Cart fetch failed (${response.status})`);
        return response.json();
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError;
  }

  function isCartAddRequest(input) {
    const url = typeof input === 'string' ? input : input?.url;
    if (!url) return false;

    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname.endsWith('/cart/add.js') || parsed.pathname.endsWith('/cart/add');
    } catch (error) {
      return false;
    }
  }

  function requestMethod(input, init) {
    return String(init?.method || input?.method || 'GET').toUpperCase();
  }

  function itemsFromFormData(body) {
    const directId = body.get('id');
    if (directId) {
      return [{
        id: directId,
        quantity: body.get('quantity') || 1,
        selling_plan: body.get('selling_plan') || '',
      }];
    }

    const indexed = {};
    body.forEach((value, key) => {
      const match = String(key).match(/^items\[(\d+)\]\[(id|quantity|selling_plan)\]$/);
      if (!match) return;
      indexed[match[1]] = indexed[match[1]] || {};
      indexed[match[1]][match[2]] = value;
    });

    return Object.keys(indexed).map((index) => indexed[index]);
  }

  function itemsFromAddBody(body) {
    if (!body) return [];
    if (body instanceof FormData) return itemsFromFormData(body).map(normalizePendingItem).filter(Boolean);
    if (body instanceof URLSearchParams) return itemsFromFormData(body).map(normalizePendingItem).filter(Boolean);

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        const rawItems = Array.isArray(parsed?.items) ? parsed.items : [parsed];
        return rawItems.map(normalizePendingItem).filter(Boolean);
      } catch (error) {
        return itemsFromFormData(new URLSearchParams(body)).map(normalizePendingItem).filter(Boolean);
      }
    }

    return [];
  }

  function registerFetchAddItems(items) {
    if (!items.length) return [];

    const groupId = eventId('add_to_cart');
    const groups = new Set();
    items.forEach((item) => {
      const pendingGroup = addPendingAdd(item, {
        event_id: groupId,
        groupId,
        source: 'fetch_cart_add',
        dedupeFreshFormSubmit: true,
      });
      if (pendingGroup) groups.add(pendingGroup);
    });

    const groupIds = Array.from(groups);
    groupIds.forEach((pendingGroupId) => {
      const groupItems = state.pendingAdds.filter((pending) => (
        (pending.groupId || pending.event_id) === pendingGroupId
      ));
      startStandardCartAdd(pendingGroupId, groupItems);
    });
    return groupIds;
  }

  function hasPendingGroup(groupId) {
    return !isGroupResolved(groupId)
      && state.pendingAdds.some((pending) => (pending.groupId || pending.event_id) === groupId);
  }

  function scheduleCartAddFallback(groupIds) {
    if (!groupIds.length) return;

    window.setTimeout(async () => {
      prunePendingAdds();
      if (!groupIds.some(hasPendingGroup)) return;

      try {
        const cart = await fetchCart();
        handleCartUpdate({
          resource: cart,
          data: { source: 'yesnap-fetch-fallback' },
        });
      } catch (error) {
        if (DEBUG) console.warn('[Yesnap ecommerce] add_to_cart fallback failed', error);
      }
    }, 600);
  }

  function patchCartAddFetch() {
    if (state.fetchPatched) return;
    state.fetchPatched = true;

    window.fetch = function yesnapTrackedFetch(input, init) {
      const shouldTrack = isCartAddRequest(input) && requestMethod(input, init) === 'POST';
      const parsedItems = shouldTrack ? itemsFromAddBody(init?.body) : [];
      const groupIds = shouldTrack ? registerFetchAddItems(parsedItems) : [];

      return nativeFetch(input, init).then((response) => {
        if (!shouldTrack) return response;
        if (!response.ok) {
          rejectStandardCartAdds(groupIds, `Unable to add item (${response.status})`);
          state.pendingAdds = state.pendingAdds.filter((pending) => !groupIds.includes(pending.groupId || pending.event_id));
          return response;
        }

        response.clone().json()
          .then((payload) => {
            if (payload?.status || payload?.errors) {
              rejectStandardCartAdds(groupIds, payload.description || payload.message || 'Unable to add item');
              state.pendingAdds = state.pendingAdds.filter((pending) => !groupIds.includes(pending.groupId || pending.event_id));
              return;
            }
            scheduleCartAddFallback(groupIds);
          })
          .catch(() => scheduleCartAddFallback(groupIds));

        return response;
      });
    };
  }

  function patchCartAddXhr() {
    if (state.xhrPatched || !window.XMLHttpRequest) return;
    state.xhrPatched = true;

    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function yesnapTrackedXhrOpen(method, url) {
      this.__ynCartAddRequest = isCartAddRequest(url) && String(method || 'GET').toUpperCase() === 'POST';
      return nativeOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function yesnapTrackedXhrSend(body) {
      const groupIds = this.__ynCartAddRequest ? registerFetchAddItems(itemsFromAddBody(body)) : [];

      if (this.__ynCartAddRequest) {
        this.addEventListener('loadend', () => {
          if (this.status < 200 || this.status >= 300) {
            rejectStandardCartAdds(groupIds, `Unable to add item (${this.status})`);
            state.pendingAdds = state.pendingAdds.filter((pending) => !groupIds.includes(pending.groupId || pending.event_id));
            return;
          }

          try {
            const payload = this.responseText ? JSON.parse(this.responseText) : null;
            if (payload?.status || payload?.errors) {
              rejectStandardCartAdds(groupIds, payload.description || payload.message || 'Unable to add item');
              state.pendingAdds = state.pendingAdds.filter((pending) => !groupIds.includes(pending.groupId || pending.event_id));
              return;
            }
          } catch (error) {
            // Non-JSON success responses still represent a successful cart add path.
          }

          scheduleCartAddFallback(groupIds);
        });
      }

      return nativeSend.apply(this, arguments);
    };
  }

  function trackViewItem() {
    const payload = readJsonScript('[data-yn-view-item]');
    if (!payload?.items?.length) return;

    pushDataLayer('view_item', {
      currency: payload.currency || window.Shopify?.currency?.active || 'USD',
      value: payload.value || 0,
      items: payload.items,
    }, { event_id: eventId('view_item') });
  }

  async function trackBeginCheckout(options) {
    if (state.beginCheckoutLocked) return false;
    state.beginCheckoutLocked = true;
    window.setTimeout(() => {
      state.beginCheckoutLocked = false;
    }, 3000);

    const embeddedCart = options?.forceCartFetch ? null : readJsonScript('[data-yn-cart-state]');
    if (embeddedCart?.items?.length) {
      pushDataLayer('begin_checkout', embeddedCart, { event_id: eventId('begin_checkout') });
      return true;
    }

    try {
      const cart = await fetchCart();
      if (state.pendingAdds.length) {
        handleCartUpdate({
          resource: cart,
          data: { source: 'begin_checkout_sync' },
        });
      }
      pushDataLayer('begin_checkout', {
        currency: cart.currency || window.Shopify?.currency?.active || 'USD',
        value: moneyFromCents(cart.total_price),
        items: (cart.items || []).map((line) => buildLineItem(line, { quantity: line.quantity })),
      }, { event_id: eventId('begin_checkout') });
      return true;
    } catch (error) {
      if (DEBUG) console.warn('[Yesnap ecommerce] begin_checkout failed', error);
      return false;
    }
  }

  document.addEventListener('submit', captureAddToCartSubmit, true);
  document.addEventListener('cart:update', (event) => handleCartUpdate(event.detail));
  document.addEventListener('click', (event) => {
    const checkoutButton = event.target.closest('[name="checkout"], .cart__checkout-button, a[href*="/checkout"]');
    if (checkoutButton) trackBeginCheckout();
  }, true);

  patchCartAddFetch();
  patchCartAddXhr();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackViewItem, { once: true });
  } else {
    trackViewItem();
  }

  window.YesnapEcommerceTracking = { initialized: true, pushDataLayer, trackBeginCheckout };
})();
